/**
 * Esteira de etapas do caso (E47.F1.T04, ADR-0058 §4): Criação · Análise
 * preliminar · Mesa · Laudo. A raia eletrônica teve a Análise fotográfica entre
 * a criação e a preliminar (E87.F2) até a E123.F3, quando as fotos e a
 * conferência facial foram para a pré-análise. Puramente APRESENTAÇÃO/navegação
 * — não cria estado de fluxo novo.
 *
 * E91.F1 (GUS-51 RF02, UXD-02): na raia eletrônica cada degrau responde a
 * TRÊS perguntas com três fontes distintas, e a esteira deixou de derivar as
 * três de um índice só:
 *
 *   - **É a página aberta?** — só a rota (`activeStage`). É o único degrau
 *     com `aria-current`. Voltar da mesa para a preliminar deixava "Mesa"
 *     como atual porque o índice alcançado mandava; agora não.
 *   - **Está concluída?** — por EVIDÊNCIA persistida: criação = caso com
 *     documentos; preliminar = alguma peça verificada; mesa = todas
 *     concluídas; laudo = homologado. Uma etapa posterior ficar disponível não
 *     conclui a anterior.
 *   - **Dá para ir?** — por pré-condição: preliminar pede documentos; mesa,
 *     alguma peça verificada; laudo, todas concluídas.
 *
 * A grafotécnica mantém a leitura pelo `workflow_status` (E23.F24), com o
 * `activeStage` desambiguando `em_analise` — o desenho de sempre.
 */
import { CheckCircle2, Circle, CircleDashed, CircleDot } from 'lucide-react';
import { Link } from 'react-router-dom';

import { ptBR } from '@/i18n/pt-BR';
import type { Modalidade, WorkflowStatus } from '@/lib/cases';
import { aguardaArquivos, rotaDoEnvioNaMesa } from '@/lib/caso_destino';
import {
  caseStageStates,
  ordemDaEsteira,
  type ActiveStage,
  type CaseStage,
  type OpcoesDaEsteira,
  type StageState,
} from '@/lib/esteira_do_caso';

export {
  caseStageStates,
  ordemDaEsteira,
  type ActiveStage,
  type CaseStage,
  type EvidenciasEletronicas,
  type StageState,
} from '@/lib/esteira_do_caso';

const STAGE_LABEL: Record<CaseStage, string> = {
  criacao: ptBR.caseStepper.stageCriacao,
  preliminar: ptBR.caseStepper.stagePreliminar,
  mesa: ptBR.caseStepper.stageMesa,
  laudo: ptBR.caseStepper.stageLaudo,
};

const STATE_CLASS: Record<StageState, string> = {
  done: 'border-success-500 bg-success-50 text-success-700 hover:bg-success-100 cursor-pointer',
  current:
    'border-accent bg-accent-bg text-accent-700 font-semibold ring-2 ring-accent cursor-pointer',
  available: 'border-border bg-bg text-fg hover:bg-bg-deep cursor-pointer',
  upcoming: 'border-border bg-bg-deep text-fg-muted opacity-70 cursor-not-allowed',
};

function StageIcon({ state }: { state: StageState }): JSX.Element {
  if (state === 'done') return <CheckCircle2 className="h-4 w-4 text-success-700" aria-hidden />;
  if (state === 'current') return <CircleDot className="h-4 w-4 text-accent" aria-hidden />;
  if (state === 'available') return <CircleDashed className="h-4 w-4 text-fg-muted" aria-hidden />;
  return <Circle className="h-4 w-4 text-fg-muted" aria-hidden />;
}

/**
 * Rota de cada etapa. E78.F1: "Criação" abre a PREPARAÇÃO do caso (o
 * assistente de envio), e não `/casos/:id`, que é o resolvedor de destino.
 */
const STAGE_ROUTE: Record<CaseStage, (caseId: string) => string> = {
  criacao: (id) => `/casos/${id}/wizard`,
  preliminar: (id) => `/casos/${id}/pre-analise`,
  mesa: (id) => `/casos/${id}/workspace`,
  laudo: (id) => `/casos/${id}/laudo`,
};

/**
 * E74.F1 (ADR-0075): a mesa da modalidade eletrônica é outra tela. E201.F1 (GUS-47, ADR-0144): a
 * pré-análise e a mesa do caso eletrônico são a mesma tela, a mesa única.
 */
function rotaDaEtapa(
  stage: CaseStage,
  caseId: string,
  modalidade: Modalidade,
  workflowStatus: WorkflowStatus | null | undefined,
): string {
  if (modalidade === 'eletronica' && (stage === 'preliminar' || stage === 'mesa')) {
    return `/casos/${caseId}/mesa`;
  }
  // E209.F3.T01 (GUS-296 item 11): o caso eletrônico que aguarda arquivos recebe o que falta na
  // mesa, não no assistente antigo.
  if (modalidade === 'eletronica' && stage === 'criacao') {
    if (aguardaArquivos({ workflow_status: workflowStatus ?? null }))
      return rotaDoEnvioNaMesa(caseId);
  }
  return STAGE_ROUTE[stage](caseId);
}

/** Etapa navegável = concluída, atual ou disponível. Futuras ficam bloqueadas. */
function isNavigable(state: StageState): boolean {
  return state !== 'upcoming';
}

const NODE_BASE =
  'inline-flex items-center gap-1.5 border px-[11px] py-1 font-sans text-[13px] tracking-[0.03em]';

export interface CaseStageStepperProps extends OpcoesDaEsteira {
  caseId: string;
  workflowStatus: WorkflowStatus | null | undefined;
  activeStage: ActiveStage;
}

export function CaseStageStepper({
  caseId,
  workflowStatus,
  activeStage,
  modalidade,
  evidencias,
}: CaseStageStepperProps): JSX.Element {
  const modalidadeEfetiva: Modalidade = modalidade ?? 'grafotecnica';
  const states = caseStageStates(workflowStatus, activeStage, {
    modalidade: modalidadeEfetiva,
    evidencias,
  });
  return (
    <nav
      className="flex flex-wrap items-center gap-2"
      aria-label={ptBR.caseStepper.ariaLabel}
      data-testid="case-stage-stepper"
      data-modalidade={modalidadeEfetiva}
    >
      <ol className="flex flex-wrap items-center gap-2">
        {ordemDaEsteira().map((stage) => {
          const state = states[stage] ?? 'upcoming';
          const shared = {
            className: `${NODE_BASE} ${STATE_CLASS[state]}`,
            'data-testid': `case-stage-${stage}`,
            'data-state': state,
          };
          return (
            <li key={stage}>
              {isNavigable(state) ? (
                <Link
                  {...shared}
                  to={rotaDaEtapa(stage, caseId, modalidadeEfetiva, workflowStatus)}
                  aria-current={state === 'current' ? 'step' : undefined}
                  title={state === 'available' ? ptBR.caseStepper.availableHint : undefined}
                >
                  <StageIcon state={state} />
                  {STAGE_LABEL[stage]}
                </Link>
              ) : (
                <span {...shared} aria-disabled="true" title={ptBR.caseStepper.blockedHint}>
                  <StageIcon state={state} />
                  {STAGE_LABEL[stage]}
                </span>
              )}
            </li>
          );
        })}
      </ol>
      {/* E149.F2 (GUS-149): a conversa com o cliente, como o histórico, fica fora das etapas. */}
      <Link
        className={`${NODE_BASE} ${STATE_CLASS.available}`}
        to={`/casos/${caseId}/conversa`}
        data-testid="case-conversa"
      >
        {ptBR.caseStepper.conversa}
      </Link>
      {/* E137.F3 (GUS-160): o histórico do caso não é etapa; fica ao fim da esteira, fora da lista. */}
      <Link
        className={`${NODE_BASE} ${STATE_CLASS.available}`}
        to={`/casos/${caseId}/historico`}
        data-testid="case-historico"
      >
        {ptBR.caseStepper.historico}
      </Link>
      {/* E157.F3 (GUS-157, ADR-0107 §2.7): a conferência dos certificados e do carimbo, só da
          equipe e só no caso eletrônico — uma tela própria sobre a visão composta no servidor. */}
      {modalidadeEfetiva === 'eletronica' ? (
        <Link
          className={`${NODE_BASE} ${STATE_CLASS.available}`}
          to={`/casos/${caseId}/certificados`}
          data-testid="case-certificados"
        >
          {ptBR.caseStepper.certificados}
        </Link>
      ) : null}
    </nav>
  );
}
